import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, useCallback } from 'react'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { PageHeader } from '@/components/ui/page-header'
import { fetchReceptionHistory } from '@/api/history.api'
import type { ReceptionHistory } from '@/types/api'
import { buildColumns } from './columns'
import { HistoryFilterPanel } from './HistoryFilterPanel'
import type { HistoryFilterValues } from './HistoryFilterPanel'
import { HistoryDetailDrawer } from './HistoryDrawer'
import { usePermission } from '@/hooks/use-permission'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import dayjs from 'dayjs'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportHistory, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: HistoryFilterValues = {
  title: '',
  customerName: '',
  unitId: undefined,
  unitName: undefined,
  robotId: undefined,
  robotName: undefined,
  dateRange: undefined,
}

function toApiParams(f: HistoryFilterValues) {
  const fromDateRaw = f.dateRange?.from
  const toDateRaw = f.dateRange?.to

  return {
    title: f.title.trim() || undefined,
    customerName: f.customerName.trim() || undefined,
    unitId: f.unitId || undefined,
    robotId: f.robotId || undefined,
    fromDate: formatStartDate(fromDateRaw || dayjs().startOf('month')),
    toDate: formatEndDate(toDateRaw || dayjs()),
  }
}

export function HistoryPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [showFilter, setShowFilter] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const canExport = usePermission('session-export')
  const canView = usePermission('session-view')

  const [draftFilter, setDraftFilter] =
    useState<HistoryFilterValues>(DEFAULT_FILTER)
  const [appliedFilter, setAppliedFilter] =
    useState<HistoryFilterValues>(DEFAULT_FILTER)

  const resolvedUnitId = showUnitColumn
    ? appliedFilter.unitId
    : currentUser?.unitId

  const apiParams = useMemo(
    () =>
      toApiParams({
        ...appliedFilter,
        unitId: resolvedUnitId,
      }),
    [appliedFilter, resolvedUnitId],
  )

  const [viewingItem, setViewingItem] = useState<ReceptionHistory | null>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reception-history', pageIndex, pageSize, apiParams],
    queryFn: ({ signal }) =>
      fetchReceptionHistory(pageIndex + 1, pageSize, apiParams, signal),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const hasData = Boolean(data?.items && data.items.length > 0)

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        showUnitColumn,
        canView,
        onViewDetail: (item) => setViewingItem(item),
      }),
    [pageIndex, pageSize, showUnitColumn, canView],
  )

  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      try {
        setIsExporting(true)
        await exportHistory(
          {
            page: pageIndex,
            size: pageSize,
            ...apiParams,
          },
          payload,
          'Danh_sach_lich_su_tiep_don',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [pageIndex, pageSize, apiParams],
  )

  const { handleExport } = useTableExport(
    'Danh sách lịch sử tiếp đón',
    columns,
    handleExportApi,
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH LỊCH SỬ TIẾP ĐÓN" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH LỊCH SỬ TIẾP ĐÓN"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
      />

      {showFilter && (
        <HistoryFilterPanel
          values={draftFilter}
          onChange={setDraftFilter}
          showUnitFilter={showUnitColumn}
          onSearch={() => {
            const trimmedFilter = {
              ...draftFilter,
              title: draftFilter.title.trim(),
              customerName: draftFilter.customerName.trim(),
            }
            setDraftFilter(trimmedFilter)
            setAppliedFilter(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        tableOptions={{
          state: {
            pagination: { pageIndex, pageSize },
          },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />

      <HistoryDetailDrawer
        item={viewingItem}
        onClose={() => setViewingItem(null)}
        showUnitColumn={showUnitColumn}
      />
    </section>
  )
}
